import { RowAction, ReviewRow } from './statementImport'

/**
 * The statement import's side of the shared merchant catalogue (see the
 * backend's MerchantCatalogue): the clean name this person would contribute
 * for a row, and which rows they contribute at all.
 *
 * Only rows matched to a budget or a bill are shared. Those are spending at a
 * merchant; a one-off can as easily be a payment to a person ("J SMITH"),
 * whose name has no business in a shared list.
 */

const NAME_ALLOWED = /^[\p{L}&'.\- ]{2,80}$/u

/**
 * What a person would call this merchant: the bank's own payee field where
 * there is one (Monzo, Starling), otherwise the matching key in title case —
 * "TESCO STORES" becomes "Tesco Stores". Digits are dropped, so no card or
 * account numbers travel. Null when nothing name-like is left.
 */
export function candidateName(row: Pick<ReviewRow, 'payee' | 'merchant'>): string | null {
  const source = row.payee && /\p{L}/u.test(row.payee) ? row.payee : titleCase(row.merchant)
  const name = source
    .replace(/[0-9*#/@:_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  return NAME_ALLOWED.test(name) && (name.match(/\p{L}/gu) ?? []).length >= 2 ? name : null
}

function titleCase(key: string): string {
  // Not after an apostrophe: Sainsbury's, not Sainsbury'S.
  return key.toLowerCase().replace(/(^|[\s&-])(\p{L})/gu, (_, sep: string, ch: string) => sep + ch.toUpperCase())
}

/** The key/name pairs to share from an applied import: rows that became a budget's or a bill's actual. */
export function namesToShare(rows: ReviewRow[], actionFor: (row: ReviewRow) => RowAction): { key: string; name: string }[] {
  const out = new Map<string, { key: string; name: string }>()
  for (const row of rows) {
    if (row.status !== 'new' || actionFor(row).kind !== 'actual' || row.merchant.length < 3) continue
    const name = candidateName(row)
    if (name) out.set(`${row.merchant}|${name}`, { key: row.merchant, name })
  }
  return [...out.values()]
}

const PREFERENCE_KEY = 'snapshot-money:share-merchant-names'

/** On unless this browser has said no: the opt-out is remembered, not asked every time. */
export function sharingPreferred(): boolean {
  try {
    return localStorage.getItem(PREFERENCE_KEY) !== 'no'
  } catch {
    return true
  }
}

export function rememberSharing(on: boolean) {
  try {
    if (on) localStorage.removeItem(PREFERENCE_KEY)
    else localStorage.setItem(PREFERENCE_KEY, 'no')
  } catch {
    // Not remembered; the box is ticked again next time.
  }
}
